/**
 * Filter and visibility state for POI rendering.
 *
 * Selection state lives in ./selection.ts (single source of truth, persisted).
 * This module handles the "show all / show only selected" filter checkbox
 * and the filtered POI view used by both the sidebar list and the map layer.
 */

// Re-export selection primitives (used by render.ts and index.ts)
export {
  isPoiSelected,
  togglePoiSelected,
  clearSelectedPois,
  restoreSelectedPois,
  getSelectedPoiIds,
  setOnSelectionChange,
} from "./selection";

import type { ParsedPoi } from "./types";
import { isPoiSelected, getSelectedPoiIds } from "./selection";
import { getPoiDetourMaxMin } from "../dom";
import L from "leaflet";

// ─── Filter / visibility state ─────────────────────────────────────────────────

let _onVisibilityChange: (() => void) | null = null;

export function setOnVisibilityChange(cb: () => void): void {
  _onVisibilityChange = cb;
}

export function notifyVisibilityChange(): void {
  _onVisibilityChange?.();
}

// ─── POI list context ─────────────────────────────────────────────────────────

let _currentPois: ParsedPoi[]   = [];
let _poiListEl:   HTMLDivElement | null = null;
let _map:         L.Map | null    = null;
let _filterCb:    HTMLInputElement | null = null;

export function setFilterCheckbox(el: HTMLInputElement): void {
  _filterCb = el;
}

export function getShowOnlySelected(): boolean {
  return _filterCb?.checked ?? false;
}

export function setShowOnlySelected(val: boolean): void {
  if (_filterCb) _filterCb.checked = val;
  notifyVisibilityChange();
}

/** Set the current POI list and DOM refs. Does NOT touch selection state. */
export function setListContext(pois: ParsedPoi[], listEl: HTMLDivElement, map: L.Map): void {
  _currentPois = pois;
  _poiListEl   = listEl;
  _map         = map;
  if (_filterCb) _filterCb.checked = false;
}

export function getCurrentPois(): ParsedPoi[] {
  return _currentPois;
}

export function getPoiListEl(): HTMLDivElement | null {
  return _poiListEl;
}

export function getMap(): L.Map | null {
  return _map;
}

// ─── Filtered view ────────────────────────────────────────────────────────────

/** Returns POIs to display based on the "show only selected" filter and detour-time cap. */
export function getVisiblePois(): ParsedPoi[] {
  const maxMin = getPoiDetourMaxMin();
  return _currentPois.filter(p => {
    if (_filterCb?.checked && !isPoiSelected(p.id)) return false;
    if (p.detour?.reachable && maxMin > 0 && p.detour.detourCostS > maxMin * 60) return false;
    return true;
  });
}

/** Number of reachable POIs currently hidden by the detour-time filter. */
export function getFilteredCount(): number {
  const maxMin = getPoiDetourMaxMin();
  if (maxMin <= 0) return 0;
  return _currentPois.filter(p =>
    p.detour?.reachable && p.detour.detourCostS > maxMin * 60
  ).length;
}

export function setSelectedCount(): { selected: number; total: number } {
  return { selected: getSelectedPoiIds().length, total: _currentPois.length };
}

// Re-export for convenience
export type { ParsedPoi };
